import React, { useEffect, useMemo, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Select, Space } from 'antd';

import 'antd/dist/reset.css';
import { List } from 'antd';

import PermissionsError from '../Errors/Permission.js';
import { convertToLocalTimezone } from '../../Helpers/Utils.js';
import { getCodeFeedUrl, getCodesAssignees } from '../../Helpers/Code.js';
import { getUsers } from '../../Helpers/User.js';
import ListPagination from '../Utils/ListPagination.js';
import UserAvatar from '../UI/Avatar.js';

const { Option } = Select;

const LANG_COLORS = {
  cpp: '#79c0ff',
  c_cpp: '#79c0ff',
  python: '#ffa657',
  java: '#ff7b9c',
  javascript: '#f0b14a',
  sql: '#d2a8ff',
};
const LANG_LABELS = {
  c_cpp: 'C++',
  cpp: 'C++',
  python: 'PY',
  java: 'JV',
  javascript: 'JS',
  sql: 'SQL',
};

function LangChip({ language }) {
  const color = LANG_COLORS[language] || 'var(--fg-1)';
  const label =
    LANG_LABELS[language] ||
    (language ? language.slice(0, 2).toUpperCase() : '??');
  return (
    <div
      className="lang-chip"
      style={{
        color,
        borderColor: `color-mix(in srgb, ${color} 30%, var(--line))`,
      }}
    >
      {label}
    </div>
  );
}

// Filter button label → backend `review_status` value.
// These uppercase strings are exactly what the backend's
// validate_code_status accepts.
const STATUS_FILTERS = [
  { key: 'all',     value: '',              label: 'All' },
  { key: 'open',    value: 'NOT_REQUESTED', label: 'Open' },
  { key: 'pending', value: 'PENDING',       label: 'Pending' },
  { key: 'solved',  value: 'SOLVED',        label: 'Solved' },
];

function CodeFeed(props) {
  const { t } = useTranslation();
  let { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the single source of truth for filters/pagination — read from it
  // every render and write back via setSearchParams. No useState mirror.
  const pageIndex          = searchParams.get('page_index')    || '1';
  const itemsPerPage       = searchParams.get('items_per_page') || '15';
  const codeReviewStatus   = searchParams.get('review_status') || '';
  const codeReviewAssignee = searchParams.get('assignee')      || '';

  const [codeFeed, setCodeFeed] = useState([]);
  const [error, setError] = useState('');
  const [totalCodes, setTotalCodes] = useState();
  const [codesAssignees, setCodesAssignees] = useState([]);
  const [users, setUsers] = useState([]);

  userId = userId === undefined ? '' : userId;

  // Fetch the feed whenever the URL filter values change. A `cancelled` flag
  // ensures a stale response from a previous filter can't overwrite the
  // current feed.
  useEffect(() => {
    let cancelled = false;
    const url = getCodeFeedUrl(
      userId,
      pageIndex,
      itemsPerPage,
      codeReviewStatus,
      codeReviewAssignee
    );
    fetch(url).then(r => r.json()).then(data => {
      if (cancelled) return;
      if (data.success === 1) {
        setError('');
        setCodeFeed(data.feed || []);
        setTotalCodes(data.total_codes);
      } else {
        setError(data.error);
      }
    });
    return () => { cancelled = true; };
  }, [userId, pageIndex, itemsPerPage, codeReviewStatus, codeReviewAssignee]);

  useEffect(() => {
    if (props.userData && props.userData.role === 'ADMIN') {
      getCodesAssignees().then(data => {
        if (data && data.success === 1) {
          setCodesAssignees(data.users || []);
        }
      });
      getUsers().then(data => {
        if (data && data.success === 1) {
          setUsers(data.users || []);
        }
      });
    }
  }, [props.userData]);

  const activeKey = useMemo(() => {
    const f = STATUS_FILTERS.find(s => s.value === codeReviewStatus);
    return f ? f.key : 'all';
  }, [codeReviewStatus]);

  function setParam(name, value) {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value == null) next.delete(name);
    else next.set(name, value);
    // any filter change resets to page 1
    if (name !== 'page_index' && name !== 'items_per_page') {
      next.set('page_index', '1');
    }
    setSearchParams(next, { replace: true });
  }

  function pickStatus(key) {
    const f = STATUS_FILTERS.find(s => s.key === key);
    setParam('review_status', f ? f.value : '');
  }

  function pickAssignee(value) {
    setParam('assignee', value || '');
  }

  // Admins can browse any user's feed: switch the route, keep the filters
  // and restart from the first page.
  function pickUser(value) {
    const next = new URLSearchParams(searchParams);
    next.set('page_index', '1');
    navigate({
      pathname: value ? `/code/feed/${value}` : '/code/feed',
      search: next.toString(),
    });
  }

  if (error) return <PermissionsError />;

  const isAdmin = props.userData && props.userData.role === 'ADMIN';

  return (
    <div className="cs-feed">
      {/* Sidebar */}
      <aside className="cs-feed__side">
        <div className="cs-side__sect">
          <div className="cs-side__title">{t('Code review status')}</div>
          {STATUS_FILTERS.map(f => {
            const tint = f.key !== 'all' ? ` cs-side__item--${f.key}` : '';
            return (
              <button
                key={f.key}
                type="button"
                className={
                  `cs-side__item${tint}` +
                  (activeKey === f.key ? ' active' : '')
                }
                onClick={() => pickStatus(f.key)}
              >
                {f.key === 'all'
                  ? <ListSvg />
                  : <span className="swatch" />}
                {t(f.label)}
              </button>
            );
          })}
        </div>

        {isAdmin && (
          <div className="cs-side__sect">
            <div className="cs-side__title">{t('Assignee')}</div>
            <div style={{ padding: '0 4px' }}>
              <Select
                id="code-assignees-select"
                placeholder={t('Assignee')}
                style={{ width: '100%' }}
                value={codeReviewAssignee || undefined}
                allowClear
                onChange={pickAssignee}
                onClear={() => pickAssignee('')}
              >
                <Option value="ALL_ADMINS">{t('All admins')}</Option>
                <Option value="NO_ASSIGNEE">{t('No assignee')}</Option>
                {codesAssignees.map((u, i) => (
                  <Option key={i} value={u}>{u}</Option>
                ))}
              </Select>
            </div>
          </div>
        )}

        <div className="cs-side__sect">
          <div className="cs-side__title">{t('scope')}</div>
          <Link
            to="/code/feed"
            className={`cs-side__item${!userId ? ' active' : ''}`}
          >
            <ListSvg /> {t('Code feed')}
          </Link>
          {props.userData && props.userData.id && (
            <Link
              to={`/code/feed/${props.userData.id}`}
              className={
                `cs-side__item` +
                (userId === props.userData.id ? ' active' : '')
              }
            >
              <UserSvg /> {t('My codes')}
            </Link>
          )}
          {isAdmin && (
            <div style={{ padding: '4px 4px 0' }}>
              <Select
                id="feed-user-select"
                showSearch
                placeholder={t('Another user')}
                style={{ width: '100%' }}
                value={userId || undefined}
                allowClear
                onChange={pickUser}
                onClear={() => pickUser('')}
              >
                {users.map((u, i) => (
                  <Option key={i} value={u}>{u}</Option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="cs-feed__main">
        <div className="cs-feed__head">
          <h1>
            {t('Code feed')}
            {' '}
            <span className="muted">
              / {userId ? `${t('user')}: ${userId}` : t('all')}
            </span>
          </h1>
          <div className="spacer" />
          <Link to="/code/add" className="cs-btn cs-btn--primary cs-btn--sm">
            <PlusSvg /> {t('Add code')}
          </Link>
        </div>

        <div className="cs-feed__filters">
          {STATUS_FILTERS.map(f => {
            const tint = f.key !== 'all' ? ` cs-filterbtn--${f.key}` : '';
            return (
              <button
                key={f.key}
                type="button"
                className={
                  `cs-filterbtn${tint}` +
                  (activeKey === f.key ? ' active' : '')
                }
                onClick={() => pickStatus(f.key)}
              >
                {f.key !== 'all' && <span className="dot" />}
                {t(f.label)}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 12.5,
              color: 'var(--fg-3)',
            }}
          >
            {totalCodes != null
              ? `${totalCodes} ${t(totalCodes === 1 ? 'review' : 'reviews')}`
              : ''}
          </span>
        </div>

        <div className="cs-feed__list">
          <List
            itemLayout="horizontal"
            dataSource={codeFeed}
            locale={{
              emptyText: (
                <div
                  style={{
                    padding: 32,
                    color: 'var(--fg-3)',
                    fontFamily: 'var(--mono)',
                    fontSize: 13,
                  }}
                >
                  {t('no reviews match these filters')}
                </div>
              ),
            }}
            renderItem={item => (
              <List.Item style={{ padding: 0, border: 'none' }}>
                <div className="cs-feed-row">
                  <LangChip language={item.language} />
                  <div className="title-col">
                    <div className="title-main">
                      <Link to={`/code/${item.id}/view`}>
                        {item.title ? item.title : item.id}
                      </Link>
                    </div>
                    <div className="title-sub">
                      <span>{convertToLocalTimezone(item.date_posted)}</span>
                      {item.id && (
                        <span>· #{String(item.id).slice(0, 8)}</span>
                      )}
                    </div>
                  </div>
                  <div className="author-col">
                    {(item.author || item.user_id) && (
                      <UserAvatar
                        user={item.author || item.user_id}
                        email={item.author_email}
                        size="xs"
                      />
                    )}
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.author || item.user_id || '—'}
                    </span>
                  </div>
                  <div
                    className={
                      `reviewer-col` +
                      (!item.assignee ? ' unassigned' : '')
                    }
                  >
                    {item.assignee
                      ? (
                        <>
                          <UserAvatar
                            user={item.assignee}
                            email={item.assignee_email}
                            size="xs"
                          />
                          <span>{item.assignee}</span>
                        </>
                      )
                      : <span>{t('unassigned')}</span>
                    }
                  </div>
                  <ReviewStatusPill status={item.review_status} />
                </div>
              </List.Item>
            )}
          />
        </div>

        <div style={{ padding: '14px 26px 18px' }}>
          <Space>
            <ListPagination
              itemsNumber={Number(itemsPerPage)}
              onItemsNumberChange={(v) => setParam('items_per_page', String(v))}
              onPageIndexChange={(v) => setParam('page_index', String(v))}
              pageIndex={Number(pageIndex)}
              totalItems={totalCodes}
            />
          </Space>
        </div>
      </div>
    </div>
  );
}

// Defensive: case-insensitive, accepts both NOT_REQUESTED and OPEN as aliases
// for the open state, and renders a neutral pill for unknown values rather
// than mislabeling them.
function ReviewStatusPill({ status }) {
  const { t } = useTranslation();
  const norm = (status || '').toString().toUpperCase();
  const map = {
    SOLVED:        { cls: 'solved',  label: 'solved' },
    PENDING:       { cls: 'pending', label: 'pending' },
    NOT_REQUESTED: { cls: 'open',    label: 'open' },
    OPEN:          { cls: 'open',    label: 'open' },
  };
  const info =
    map[norm] ||
    (norm
      ? { cls: 'open', label: norm.toLowerCase() }
      : { cls: 'open', label: '—' });
  return (
    <span className={`cs-pill cs-pill--${info.cls}`}>
      <span className="dot" />
      {t(info.label)}
    </span>
  );
}

function ListSvg() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function PlusSvg() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function UserSvg() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 21a8 8 0 1 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
    </svg>
  );
}

export default CodeFeed;
